import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ChevronRight, Sparkles } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Prefooter, Footer } from '@/components/layout'

const ASSET_BASE = '/images/campaigns/spring-collection-2026'

export function LandingCampagnaPage() {
  const { t } = useTranslation('common')

  const essentials = [
    { name: t('landing.product1'), image: `${ASSET_BASE}/shampoo.png`, href: '/prodotti/shampoo' },
    { name: t('landing.product2'), image: `${ASSET_BASE}/conditioner.png`, href: '/prodotti/conditioner' },
    { name: t('landing.product3'), image: `${ASSET_BASE}/oil.png`, href: '/prodotti/oil' },
    { name: t('landing.product4'), image: `${ASSET_BASE}/instant.png`, href: '/prodotti/instant' },
    { name: t('landing.product5'), image: `${ASSET_BASE}/deep-infusion.png`, href: '/prodotti/deep-infusion' },
  ]

  return (
    <main className="bg-cream">
      {/* Hero cinematografico */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-16 select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(16rem, 32vw, 36rem)', lineHeight: 0.85, letterSpacing: '-0.04em' }}
        >
          {t('landing.heroWatermark')}
        </div>

        <div className="relative nashi-container">
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            aria-label={t('landing.breadcrumbLabel')}
            className="mb-12 flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-bruno-muted"
          >
            <LocalizedLink to="home" className="hover:text-argan-deep transition-colors">
              {t('nav.home')}
            </LocalizedLink>
            <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
            <span className="text-bruno truncate">{t('landing.breadcrumbCurrent')}</span>
          </motion.nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-6"
            >
              <span className="nashi-eyebrow">{t('landing.heroEyebrow')}</span>
              <h1
                className="mt-8 font-display text-bruno"
                style={{
                  fontSize: 'clamp(3rem, 7vw, 6.5rem)',
                  lineHeight: 0.94,
                  letterSpacing: '-0.025em',
                }}
              >
                <span className="block">{t('landing.heroTitle1')}</span>
                <span className="block">{t('landing.heroTitle2')}</span>
                <span className="block italic font-light text-argan-deep">{t('landing.heroTitle3')}</span>
                <span className="block tabular-nums text-argan">{t('landing.heroTitle4')}</span>
              </h1>
              <p className="mt-10 max-w-md text-bruno-soft text-base md:text-lg leading-relaxed">
                {t('landing.heroBody')}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#essentials"
                  className="nashi-cta inline-flex items-center justify-center gap-2 px-7 py-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] hover:bg-argan-deep transition-colors duration-500"
                >
                  {t('landing.ctaPrimary')}
                  <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                </a>
                <a
                  href="#salon"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-bruno/30 text-bruno text-[11px] uppercase tracking-[0.2em] hover:border-argan hover:text-argan-deep transition-colors duration-500"
                >
                  {t('landing.ctaSecondary')}
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-6 relative"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={`${ASSET_BASE}/hero.jpg`}
                  alt={t('landing.heroImageAlt')}
                  loading="eager"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-bruno/30 via-transparent to-transparent"
                />
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <span className="nashi-numeral text-cream/90">{t('landing.heroNumeral')}</span>
                  <span className="text-[10px] uppercase tracking-[0.32em] text-cream/80">
                    {t('landing.heroTag')}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro tipografico full-bleed */}
      <section className="relative bg-cream-soft py-24 md:py-36 overflow-hidden border-y border-bruno/10">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 select-none font-display italic text-argan/[0.05] whitespace-nowrap"
          style={{ fontSize: 'clamp(10rem, 20vw, 22rem)', lineHeight: 1, letterSpacing: '-0.04em' }}
        >
          {t('landing.introWatermark')}
        </div>

        <div className="relative nashi-container max-w-5xl text-center">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8 }}
            className="nashi-eyebrow inline-block"
          >
            {t('landing.introEyebrow')}
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="mt-8 font-display text-bruno"
            style={{
              fontSize: 'clamp(2.5rem, 5.5vw, 4.75rem)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            <span className="block">{t('landing.introTitle1')}</span>
            <span className="block italic font-light text-argan-deep">{t('landing.introTitle2')}</span>
            <span className="block">{t('landing.introTitle3')}</span>
            <span className="block italic font-light text-argan-deep">{t('landing.introTitle4')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-bruno-muted"
          >
            <Sparkles className="h-4 w-4 text-argan" strokeWidth={1.5} />
            {t('landing.introFragrance')}
            <Sparkles className="h-4 w-4 text-argan" strokeWidth={1.5} />
          </motion.p>
        </div>
      </section>

      {/* Essentials grid */}
      <section
        id="essentials"
        aria-label={t('landing.essentialsAria')}
        className="relative bg-cream py-24 md:py-32 overflow-hidden"
      >
        <div className="relative nashi-container">
          <div className="flex items-end justify-between mb-14 md:mb-20 pb-6 border-b border-bruno/10">
            <div>
              <span className="text-[10px] uppercase tracking-[0.32em] text-argan-deep">
                {t('landing.essentialsCapsule')}
              </span>
              <h2
                className="mt-3 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                }}
              >
                {t('landing.essentialsTitle1')}{' '}
                <span className="italic font-light text-argan-deep">{t('landing.essentialsTitle2')}</span>
              </h2>
            </div>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.32em] text-bruno-muted tabular-nums">
              {t('landing.essentialsCount')}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-16">
            {essentials.map((p, idx) => (
              <motion.article
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: (idx % 3) * 0.08 }}
                className="group relative"
              >
                <LocalizedLink to={p.href} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-argan focus-visible:ring-offset-4 focus-visible:ring-offset-cream">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <span
                      aria-hidden="true"
                      className="nashi-numeral absolute top-4 left-5 z-10 text-bruno-muted/80 transition-colors duration-500 group-hover:text-argan-deep"
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_60%,rgba(207,169,128,0.12),transparent_60%)] opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                    />
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>

                  <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                      {t('landing.productLine')}
                    </p>
                    <h3 className="mt-2 font-display italic text-bruno text-xl md:text-2xl leading-tight transition-colors duration-500 group-hover:text-argan-deep">
                      {p.name.replace(` ${t('landing.productLine')}`, '')}
                    </h3>
                    <span className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-bruno-soft transition-colors duration-500 group-hover:text-argan-deep">
                      {t('landing.discoverMore')} <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                    </span>
                  </div>

                  <span className="mt-3 block h-px w-0 bg-argan transition-all duration-700 ease-out group-hover:w-full" />
                </LocalizedLink>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial banner full-bleed */}
      <section className="relative w-full overflow-hidden">
        <div className="relative aspect-[16/9] md:aspect-[21/9]">
          <img
            src={`${ASSET_BASE}/feature.jpg`}
            alt={t('landing.bannerAlt')}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-bruno/55 via-bruno/20 to-transparent"
          />
          <div className="absolute inset-0 flex items-center">
            <div className="nashi-container">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 1 }}
                className="max-w-xl"
              >
                <span className="text-[10px] uppercase tracking-[0.32em] text-argan-soft">
                  {t('landing.bannerTag')}
                </span>
                <p
                  className="mt-5 font-display italic text-cream"
                  style={{ fontSize: 'clamp(1.75rem, 3.6vw, 3.25rem)', lineHeight: 1.05 }}
                >
                  {t('landing.bannerText1')}
                  <br />
                  {t('landing.bannerText2')}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Salon CTA */}
      <section
        id="salon"
        aria-label={t('landing.salonAria')}
        className="relative bg-cream-soft py-24 md:py-32 overflow-hidden"
      >
        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="lg:col-span-7"
            >
              <span className="nashi-eyebrow">{t('landing.salonEyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2.25rem, 4.2vw, 3.75rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.02em',
                }}
              >
                <span className="block">{t('landing.salonTitle1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('landing.salonTitle2')}
                </span>
              </h2>
              <p className="mt-8 max-w-xl text-bruno-soft text-base md:text-lg leading-relaxed">
                {t('landing.salonBody')}
              </p>
              <LocalizedLink
                to="storeLocator"
                className="nashi-cta mt-10 inline-flex items-center justify-center gap-2 px-7 py-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] hover:bg-argan-deep transition-colors duration-500"
              >
                {t('landing.salonCta')}
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
              </LocalizedLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={`${ASSET_BASE}/hero.jpg`}
                  alt={t('landing.salonImageAlt')}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-bruno/30 via-transparent to-transparent"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing — archive link */}
      <section className="relative bg-cream py-20 md:py-28 overflow-hidden border-t border-bruno/10">
        <div className="relative nashi-container max-w-3xl text-center">
          <span className="nashi-eyebrow">{t('landing.archiveEyebrow')}</span>
          <h2
            className="mt-6 font-display text-bruno"
            style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
            }}
          >
            {t('landing.archiveTitle1')}{' '}
            <span className="italic font-light text-argan-deep">{t('landing.archiveTitle2')}</span>
          </h2>
          <LocalizedLink
            to="productsHair"
            className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-bruno hover:text-argan-deep transition-colors border-b border-bruno/30 hover:border-argan pb-2"
          >
            {t('landing.archiveCta')}
            <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
          </LocalizedLink>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
